import React, { FC, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import * as styles from "../RightSideWrapper/RightSideWrapper.module.css";
import { useRightSidePortal } from "./RightSidePortalContext";

type RightSideWrapperProps = {
  rightSideComponent: JSX.Element;
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  children: ReactNode;
};

const SideCardContainer: FC<{ children: ReactNode }> = ({ children }) => {
  const cardContentRef = useRef<HTMLDivElement>(null);
  const sideCardRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const outsideCardWidthRef = useRef<number | null>(null);
  const [hasContent, setHasContent] = useState(true);
  const [isInline, setIsInline] = useState(false);

  useEffect(() => {
    if (cardContentRef.current && cardContentRef.current.childElementCount === 0) {
      setHasContent(false);
    }
  }, []);

  useEffect(() => {
    const updatePlacement = () => {
      const sideCard = sideCardRef.current;
      if (!sideCard) {
        return;
      }

      if (!anchorRef.current) {
        anchorRef.current = sideCard.offsetParent as HTMLElement | null;
      }

      if (!outsideCardWidthRef.current) {
        outsideCardWidthRef.current = sideCard.getBoundingClientRect().width;
      }

      const anchor = anchorRef.current;
      const outsideCardWidth = outsideCardWidthRef.current;
      if (!anchor || !outsideCardWidth) {
        return;
      }

      const rootFontSize = Number.parseFloat(
        window.getComputedStyle(document.documentElement).fontSize
      );
      const outsideLeft = anchor.getBoundingClientRect().left - outsideCardWidth - rootFontSize * 2;
      setIsInline(outsideLeft < 0);
    };

    updatePlacement();
    window.addEventListener("resize", updatePlacement);
    return () => window.removeEventListener("resize", updatePlacement);
  }, []);

  if (!hasContent) {
    return null;
  }

  return (
    <div
      className={`${styles.sideCard} ${isInline ? styles.inlineSideCard : ""}`}
      ref={sideCardRef}
    >
      <div className={styles.portalCard} ref={cardContentRef}>
        {children}
      </div>
    </div>
  );
};

export const RightSideWrapper: FC<RightSideWrapperProps> = ({ rightSideComponent, children }) => {
  const [isReady, setIsReady] = useState(false);
  const portalTargetId = useRightSidePortal();

  useEffect(() => {
    setIsReady(true);
  }, []);

  if (!isReady) {
    return <>{children}</>;
  }

  if (portalTargetId) {
    const portalTarget = document.getElementById(portalTargetId);
    return (
      <div className={styles.wrapper}>
        {children}
        {portalTarget &&
          createPortal(<SideCardContainer>{rightSideComponent}</SideCardContainer>, portalTarget)}
      </div>
    );
  }

  return <div className={styles.wrapper}>{children}</div>;
};
